
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Plus, Users, List, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrelloColumn } from "./TrelloColumn";
import { TrelloCardDialog } from "./TrelloCardDialog";
import { WorkflowMembersDialog } from "./WorkflowMembersDialog";
import { ShareBoardDialog } from "./ShareBoardDialog";
import { supabase } from "@/integrations/supabase/client";
import { KanbanColumn, KanbanCard } from "@/types/database";
import { toast } from "@/lib/toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListView } from "./board/ListView";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface TrelloBoardProps {
  boardId: string;
}

export function TrelloBoard({ boardId }: TrelloBoardProps) {
  const isMobile = useIsMobile();
  const [selectedCard, setSelectedCard] = useState<KanbanCard | null>(null);
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");
  const [isAddingColumn, setIsAddingColumn] = useState(false);
  const [activeView, setActiveView] = useState("board");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: columns = [], isLoading: columnsLoading, refetch: refetchColumns } = useQuery({
    queryKey: ["kanban-columns", boardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_columns")
        .select(`
          *,
          goals:goal_id (
            id,
            title,
            current_value,
            target_value,
            completed
          )
        `)
        .eq("board_id", boardId)
        .order("position");
      
      if (error) throw error;
      return data as (KanbanColumn & { goals?: any })[];
    },
    enabled: !!boardId
  });

  const { data: cards = [], isLoading: cardsLoading, refetch: refetchCards } = useQuery({
    queryKey: ["kanban-cards", boardId],
    queryFn: async () => {
      if (!columns.length) return [];
      
      const columnIds = columns.map(col => col.id);
      const { data, error } = await supabase
        .from("kanban_cards")
        .select("*")
        .in("column_id", columnIds)
        .eq("is_archived", false)
        .order("position");
      
      if (error) throw error;
      return data as KanbanCard[];
    },
    enabled: !!boardId && columns.length > 0
  });

  const { data: boardData } = useQuery({
    queryKey: ["kanban-board", boardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_boards")
        .select("name")
        .eq("id", boardId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!boardId
  });

  const refreshCards = () => {
    refetchCards();
  };

  const refreshColumns = () => {
    refetchColumns();
    refetchCards();
  };

  const addColumnMutation = useMutation({
    mutationFn: async (name: string) => {
      const position = columns.length + 1;
      const { data, error } = await supabase
        .from("kanban_columns")
        .insert({
          board_id: boardId,
          name,
          position
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-columns", boardId] });
      setNewColumnName("");
      setIsAddingColumn(false);
      toast.success("Coluna adicionada com sucesso!");
    },
    onError: (error) => {
      console.error("Error adding column:", error);
      toast.error("Erro ao adicionar coluna");
    }
  });

  const updateCardMutation = useMutation({
    mutationFn: async (cardData: Partial<KanbanCard>) => {
      const { error } = await supabase
        .from("kanban_cards")
        .update(cardData)
        .eq("id", cardData.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kanban-cards", boardId] });
      toast.success("Card atualizado com sucesso!");
    },
    onError: (error) => {
      console.error("Error updating card:", error);
      toast.error("Erro ao atualizar card");
    }
  });

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (type === "column") {
      const reorderedColumns = Array.from(columns);
      const [movedColumn] = reorderedColumns.splice(source.index, 1);
      reorderedColumns.splice(destination.index, 0, movedColumn);

      const updatedColumns = reorderedColumns.map((col, index) => ({
        ...col,
        position: index + 1
      }));

      queryClient.setQueryData(["kanban-columns", boardId], updatedColumns);

      try {
        for (const col of updatedColumns) {
          const { error } = await supabase
            .from('kanban_columns')
            .update({ position: col.position })
            .eq('id', col.id);

          if (error) throw error;
        }

        toast.success('Ordem das colunas atualizada!');
      } catch (error) {
        console.error('Error updating column positions:', error);
        toast.error('Erro ao atualizar ordem das colunas');
        queryClient.setQueryData(["kanban-columns", boardId], columns);
      }
      return;
    }

    const card = cards.find(c => c.id === draggableId);
    if (!card) return;

    try {
      const { error } = await supabase
        .from("kanban_cards")
        .update({
          column_id: destination.droppableId,
          position: destination.index + 1
        })
        .eq("id", draggableId);

      if (error) throw error;
      
      refreshCards();
      refreshColumns();
      
      // Também atualizar as metas das colunas envolvidas
      const sourceColumn = columns.find(col => col.id === source.droppableId);
      const destColumn = columns.find(col => col.id === destination.droppableId);
      
      // Recalcular progresso para coluna de origem se for coluna meta
      if (sourceColumn?.is_goal_column && sourceColumn.goal_id) {
        const sourceCards = cards.filter(c => c.column_id === source.droppableId && c.id !== draggableId);
        
        await supabase
          .from('goals')
          .update({
            current_value: sourceCards.length,
            updated_at: new Date().toISOString()
          })
          .eq('id', sourceColumn.goal_id);
      }
      
      // Recalcular progresso para coluna de destino se for coluna meta
      if (destColumn?.is_goal_column && destColumn.goal_id) {
        const destCards = cards.filter(c => c.column_id === destination.droppableId);
        const newCount = destCards.length + 1;
        
        const { data: goalData } = await supabase
          .from('goals')
          .select('target_value')
          .eq('id', destColumn.goal_id)
          .single();
        
        if (goalData) {
          await supabase
            .from('goals')
            .update({
              current_value: newCount,
              completed: newCount >= goalData.target_value,
              updated_at: new Date().toISOString()
            })
            .eq('id', destColumn.goal_id);
        }
      }
      
    } catch (error) {
      console.error("Error moving card:", error);
      toast.error("Erro ao mover card");
    }
  };

  const handleCardClick = (card: KanbanCard) => {
    setSelectedCard(card);
    setCardDialogOpen(true);
  };

  const handleCardUpdate = async (cardData: Partial<KanbanCard>) => {
    await updateCardMutation.mutateAsync(cardData);
  };

  const handleAddColumn = () => {
    if (newColumnName.trim()) {
      addColumnMutation.mutate(newColumnName.trim());
    }
  };

  const handleMindMapClick = () => {
    navigate(`/mindmap/${boardId}`);
  };

  if (columnsLoading || cardsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className={cn(
        "flex items-center justify-between mb-6",
        isMobile && "flex-col gap-4"
      )}>
        <Tabs value={activeView} onValueChange={setActiveView} className={cn(
          isMobile && "w-full"
        )}>
          <TabsList className={cn(
            isMobile && "grid w-full grid-cols-2"
          )}>
            <TabsTrigger value="board" className="flex items-center gap-2">
              <div className="w-4 h-4 bg-current rounded-sm" />
              Board
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              Lista
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className={cn(
          "flex items-center gap-2",
          isMobile && "w-full justify-between"
        )}>
          {isMobile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleMindMapClick}>
                  <div className="w-4 h-4 mr-2 rounded-full bg-purple-500"></div>
                  Mapa Mental
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMembersDialogOpen(true)}>
                  <Users className="w-4 h-4 mr-2" />
                  Membros
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMindMapClick}
                className="bg-purple-50 hover:bg-purple-100 border-purple-200"
              >
                <div className="w-4 h-4 mr-2 rounded-full bg-purple-500"></div>
                Mapa Mental
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMembersDialogOpen(true)}
              >
                <Users className="w-4 h-4 mr-2" />
                Membros
              </Button>

              <ShareBoardDialog 
                boardId={boardId} 
                boardName={boardData?.name || "Quadro"} 
              />
            </>
          )}

          {isMobile && (
            <ShareBoardDialog 
              boardId={boardId} 
              boardName={boardData?.name || "Quadro"} 
            />
          )}
        </div>
      </div>

      <Tabs value={activeView} className="h-full">
        <TabsContent value="board" className="h-full mt-0">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="board" direction="horizontal" type="column">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={cn(
                    "flex gap-6 overflow-x-auto pb-6 h-full",
                    isMobile && "gap-3 px-4"
                  )}
                  style={isMobile ? {
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch'
                  } : {}}
                >
                  {columns.map((column, index) => (
                    <Draggable key={column.id} draggableId={column.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            snapshot.isDragging ? "opacity-50" : "",
                            isMobile && "scroll-snap-align-start flex-shrink-0"
                          )}
                          style={{
                            ...provided.draggableProps.style,
                            ...(isMobile && { minWidth: '280px', maxWidth: '280px' })
                          }}
                        >
                          <TrelloColumn
                            column={column}
                            cards={cards.filter(card => card.column_id === column.id)}
                            boardId={boardId}
                            index={index}
                            onRefreshCards={refreshCards}
                            onRefreshColumns={refreshColumns}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  
                  <div className={cn(
                    "min-w-[280px]",
                    isMobile && "flex-shrink-0"
                  )}>
                    {isAddingColumn ? (
                      <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                        <Input
                          value={newColumnName}
                          onChange={(e) => setNewColumnName(e.target.value)}
                          placeholder="Nome da coluna..."
                          className="mb-2"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleAddColumn();
                            }
                          }}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={handleAddColumn}
                            disabled={!newColumnName.trim() || addColumnMutation.isPending}
                          >
                            Adicionar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => {
                              setIsAddingColumn(false);
                              setNewColumnName("");
                            }}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setIsAddingColumn(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar coluna
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </TabsContent>

        <TabsContent value="list" className="h-full mt-0">
          <ListView 
            cards={cards} 
            columns={columns}
            boardId={boardId}
            onRefreshCards={refreshCards}
          />
        </TabsContent>
      </Tabs>

      {selectedCard && (
        <TrelloCardDialog
          open={cardDialogOpen}
          onOpenChange={setCardDialogOpen}
          card={selectedCard}
          mode="edit"
          boardId={boardId}
          columnId={selectedCard.column_id}
          onCardSaved={refreshCards}
          trigger={<div />}
        />
      )}

      <WorkflowMembersDialog 
        open={membersDialogOpen}
        onOpenChange={setMembersDialogOpen}
        boardId={boardId}
        boardName={boardData?.name || "Board"}
      />
    </div>
  );
}
